/**
 * Convert Cloudinary URLs to domain-branded URLs
 */
export function getBrandedImageUrl(cloudinaryUrl: string | null | undefined): string | null {
  if (!cloudinaryUrl) return null;
  
  // If it's already a branded URL, return as-is
  if (!cloudinaryUrl.includes('res.cloudinary.com')) {
    return cloudinaryUrl;
  }
  
  // Extract the path after cloudinary domain and cloud name
  const cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;
  const cloudinaryBase = `https://res.cloudinary.com/${cloudName}/`;
  
  if (!cloudinaryUrl.startsWith(cloudinaryBase)) {
    return cloudinaryUrl; // Return original if pattern doesn't match
  }
  
  // Remove any existing query parameters (transformations) from the original URL
  const cleanUrl = cloudinaryUrl.split('?')[0];
  const imagePath = cleanUrl.replace(cloudinaryBase, '');
  return `/images/${imagePath}`;
}

/**
 * Get optimized image URL with transformations
 * For use with Next.js Image component - returns base branded URL without transformations
 * Let Next.js handle the optimization
 */
export function getOptimizedImageUrl(
  cloudinaryUrl: string | null | undefined,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png';
    crop?: 'fill' | 'fit' | 'scale' | 'crop';
  } = {}
): string | null {
  // For Next.js Image component, just return the branded URL without transformations
  // Next.js will handle the optimization
  return getBrandedImageUrl(cloudinaryUrl);
}

/**
 * Get direct optimized image URL with transformations (for non-Next.js Image usage)
 */
export function getDirectImageUrl(
  cloudinaryUrl: string | null | undefined,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png';
    crop?: 'fill' | 'fit' | 'scale' | 'crop';
  } = {}
): string | null {
  const baseUrl = getBrandedImageUrl(cloudinaryUrl);
  if (!baseUrl) return null;
  
  const params = new URLSearchParams();
  
  if (options.width) params.set('w', options.width.toString());
  if (options.height) params.set('h', options.height.toString());
  if (options.quality) params.set('q', options.quality.toString());
  if (options.format) params.set('f', options.format);
  if (options.crop) params.set('c', options.crop);
  
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}